import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { catchError, map, of, pairwise, startWith, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Application } from 'src/portaljs/constants/application';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { PortalApp } from 'src/app/core/shared/portal-app';
import Icons from 'src/app/core/enums/icons.enum';
import {AuthService} from 'src/app/core/services/auth.service';
import {UserLoginModel} from 'src/app/core/models/user-login.model';
import {GrantsModel} from 'src/app/core/models/grants.model';
import {SiteModel, SitesModel} from 'src/app/core/models/sites.model';
import {UserService} from 'src/app/core/services/user.service';
import {UserMessage} from 'src/app/core/shared/error/user-message';
import {ErrorDialogService} from 'src/app/core/services/error-dialog.service';
import {DialogModeType} from 'src/app/core/shared/error/dialog-message-type.enum';
import {ClientLoginService} from '../../core/services/client-login.service';
import PopupState from '../../core/enums/popup.state.enum';
import {ConsoleLogger} from 'src/app/core/shared/console-logger';
import * as _ from 'lodash-es';
import { Message } from 'src/portaljs/models/message';
import LoginPrompt from 'src/app/core/enums/login-prompt.enum';
import { ToastMessageService } from 'src/app/core/services/toast-message.service';
import { Util } from 'src/app/core/shared/util';
import { LoginAction } from 'src/app/core/models/login-action';
import { InitialStateService } from 'src/app/core/services/initial-state-service';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { SidenavMenuStates } from 'src/app/core/enums/sidenav-menu-states.enum';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { UnifiedSettingsService } from 'src/app/core/services/unified-settings.service';
import { IframeService } from 'src/app/core/services/iframe.service';

@Component({
    selector: 'fss-portal-root',
    templateUrl: './portal.component.html',
    styleUrls: ['./portal.component.scss'],
    host: { id: 'fss-portal' },
    standalone: false
})
export class PortalComponent implements OnInit, OnDestroy {
  public readonly embeddableFrames: PortalApp[] = this.iframeService.getEmbeddedFrames(); // DO NOT change this array reference.
  private ngUnsubscribe = new Subject();
  public isFunEnabled = this.envService.isFUN_ENABLED();

  public Icons = Icons;
  public apps = Application;

  public visibleApp: Application;
  public portalEnabled = true; // Page is not visible if there is no District information provided from the sites call or it is flagged as disabled
  public showLandingPage = false;
  public showSiteList = false;
  public showAppSubMenu = false;
  private showDashboard = false;
  public loginPopupOpen = false;
  public queryParamApp: Application;
  public destinyUserResetRequestReason = this.initStateService.destinyUserResetRequestReason;
  public viaLogin = this.initStateService.loginComponentInitialized;
  public offset: string;
  public showSpinner$ = this.portalStateService.getShowingLoadingSpinner$();

  @ViewChild('drawerContainer') drawerContainer: MatDrawerContainer;
  @ViewChild('matDrawer') matDrawer: MatDrawer;

  public updateDrawerContainerSize() {
    if (this.drawerContainer) {
      const matDrawer = document.getElementById('sidenav-menu');
      let drawerMargin = 0;
      if (this.portalStateService.getMenuStateForApp() !== SidenavMenuStates.HIDDEN) {
        drawerMargin = parseFloat(getComputedStyle(matDrawer).width);
      }
      this.drawerContainer._contentMargins.left = drawerMargin;
      this.offset = this.drawerContainer._contentMargins.left - 16 + 'px';
    }
  }

  public get isMobile(): boolean {
    return this.toolbarService.isMobile();
  }

  public isExpandButtonShowing() {
    return !this.isMobile && this.isShowingSidenav() && this.isUnEnabledForCurrentApp() && this.portalStateService.getMenuStateForApp() !== SidenavMenuStates.LARGE_ICONS;
  }

  public toggleSidenav() {
    this.toolbarService.toggleSidenav();
  }

  public isUnEnabledForCurrentApp() {
    return this.portalStateService.isUnEnabledForCurrentApp();
  }

  constructor(
    private portalStateService: PortalStateService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userService: UserService,
    private errorDialogService: ErrorDialogService,
    private clientLoginService: ClientLoginService,
    private toastService: ToastMessageService,
    private initStateService: InitialStateService,
    public toolbarService: ToolbarService,
    private envService: EnvironmentService,
    private unsService: UnifiedSettingsService,
    private iframeService: IframeService) {
  }


  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public ngOnInit(): void {
    ConsoleLogger.debug('xxxx - PortalComponent[0].ngOnInit(): called');
    this.unsService.fetchUnifiedDistrictSettings(this.authService.getDistrictAppId())
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe();

    if (!_.isEmpty(this.destinyUserResetRequestReason)) {
      ConsoleLogger.debug('xxxx - PortalComponent[1].ngOnInit(): destinyUserResetRequestReason not empty');
      // User chose forgot_password or create_account. Load Destiny iframe, if needed, and send reset action
      this.visibleApp = Application.DESTINY;

      const msg: Message = {
        source: Application.PORTAL, destination: Application.DESTINY,
        message: { fss_reason: this.destinyUserResetRequestReason, siteGuid: this.authService.getSiteGuid() }
      } as any;


      this.iframeService.DESTINY_RESET_IN_PROGRESS_HACK_INDICATOR = true;
      this.portalStateService.initiateDestinyUserAccountChange(msg)
        .pipe(
          catchError(() => {
            ConsoleLogger.debug('xxxx - PortalComponent[2].ngOnInit(): catchError in initiateDestinyUserAccountChange');
            this.iframeService.DESTINY_RESET_IN_PROGRESS_HACK_INDICATOR = false;
            return of(undefined);
          }),
          take(1),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(() => {
          ConsoleLogger.debug('xxxx - PortalComponent[3].ngOnInit(): Destiny reset complete');
          this.iframeService.DESTINY_RESET_IN_PROGRESS_HACK_INDICATOR = false;
        });

      this.handleActiveApp();
      this.portalStateService.setShowLoadingSpinner(false);
    } else {
      if (this.viaLogin) {
        ConsoleLogger.debug('xxxx - PortalComponent[4].ngOnInit(): viaLogin true');
        this.handleUserChange();
        this.handleQueryParams();
        this.handleActiveApp();
        this.handlePopupState();
      } else {
        ConsoleLogger.debug('xxxx - PortalComponent[5].ngOnInit(): viaLogin false');
        // Navigate back to LoginComponent to handle bookmarks, etc.
        this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
      }
    }
  }

  public isSidenavExpanded() {
    return this.portalStateService.getMenuStateForApp() !== SidenavMenuStates.STANDARD;
  }

  public isShowingSidenav() {
    return (this.portalStateService.getMenuStateForApp() !== SidenavMenuStates.HIDDEN && !_.isEmpty(this.portalStateService.activeMenuItems)) || this.toolbarService.portal !== undefined;
  }

  public isOldMobileSidenavOpen() {
    return this.toolbarService.isMobile && this.toolbarService.isMobileMenuOpen;
  }

  private handleUserChange() {
    ConsoleLogger.debug('xxxx - PortalComponent[6].handleUserChange(): called');
    this.authService.getGrants$()
      .pipe(
        tap(() => {
          this.showAppSubMenu = false;
          this.showSiteList = false;
          this.portalStateService.setShowLoadingSpinner(true);
        }),
        switchMap((grants: GrantsModel) => {
          ConsoleLogger.debug('xxxx - PortalComponent[7].handleUserChange(): switchMap grants');
          const ulm: UserLoginModel = this.authService.getUserLogin();
          return this.userService.fetchSitesList(ulm)
            .pipe(
              map((sites: SitesModel) => [grants, sites]),
              catchError(error => {
                ConsoleLogger.error('xxxx - PortalComponent[8].handleUserChange(): Failed to get Sites for user: ' + JSON.stringify(error));
                return of([grants, undefined]);
              })
            );
        }),
        catchError(error => {
          ConsoleLogger.error('xxxx - PortalComponent[9].handleUserChange(): Failed to get Grants for user: ' + JSON.stringify(error));
          return of([undefined, undefined]);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([grants, sites]) => {
        ConsoleLogger.debug('xxxx - PortalComponent[10].handleUserChange(): subscribe grants/sites');
        this.handleDistrictInfo(sites);

        const ulm: UserLoginModel = this.authService.getUserLogin();
        if (!grants || !ulm) {
          ConsoleLogger.debug('xxxx - PortalComponent[11].handleUserChange(): grants or ulm undefined');
          this.handleUndefinedUser();
        } else {
          ConsoleLogger.debug('xxxx - PortalComponent[12].handleUserChange(): grants and ulm defined');
          const siteList: SiteModel[] = _.get(sites, 'sites', []);
          this.handleAuthenticatedUserButWait(grants as GrantsModel, siteList);
        }
        this.portalStateService.setShowLoadingSpinner(false);
      });
  }


  private handleDistrictInfo(sites: SitesModel) {
    ConsoleLogger.debug('xxxx - PortalComponent[13].handleDistrictInfo(): called');
    const bypassPortalEnableCheck = localStorage.getItem('enable-portal');
    if(bypassPortalEnableCheck) {
      ConsoleLogger.debug('xxxx - PortalComponent[14].handleDistrictInfo(): bypassPortalEnableCheck true');
      this.portalEnabled = true;
    } else {
      if (sites) {
        ConsoleLogger.debug('xxxx - PortalComponent[15].handleDistrictInfo(): sites defined');
        this.portalEnabled = !!_.get(sites, ['district', 'portalEnabled']);
        if (!this.portalEnabled) {
          ConsoleLogger.debug('xxxx - PortalComponent[16].handleDistrictInfo(): portalEnabled false');
          this.errorDialogService.open( DialogModeType.ERROR, UserMessage.portalNotEnabled, undefined, UserMessage.genericFailureMsg, undefined, false, false, true );
        }
      }
    }
  }


  private handleUndefinedUser() {
    this.portalStateService.initializeAllMenuItems();
    this.portalStateService.initializeSecondaryToolbars();
    this.showLandingPage = true;

    if(this.visibleApp === Application.PORTAL){ // if at the district homepage
      if(this.authService.getUserLogin() && this.authService.getSiteGuid()) {
        ConsoleLogger.debug('xxxx - PortalComponent[17].handleUndefinedUser(): both siteGuid and ulm exist');
        this.showSiteList = false;
      } else {
        ConsoleLogger.debug('xxxx - PortalComponent[18].handleUndefinedUser(): siteGuid and ulm do not exist');
        this.showSiteList = true;
      }
    } else { // anywhere other than district homepage
      ConsoleLogger.debug('xxxx - PortalComponent[18a].handleUndefinedUser(): both siteGuid and ulm exist');
      this.showSiteList = false;
    }
    this.showDashboard = false;
    this.showAppSubMenu = false;
  }


  private handleAuthenticatedUserButWait(grants: GrantsModel, sites: SiteModel[]) {
    ConsoleLogger.debug('xxxx - PortalComponent[19].handleAuthenticatedUserButWait(): called');
    if (grants) {
      ConsoleLogger.debug('xxxx - PortalComponent[20].handleAuthenticatedUserButWait(): grants defined');
      this.portalStateService.waitForLoginProcessToComplete()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          ConsoleLogger.debug('xxxx - PortalComponent[21].handleAuthenticatedUserButWait(): done waiting');
          this.handleAuthenticatedUser(grants, sites);
        });
    }
  }

  private handleAuthenticatedUser(grants: GrantsModel, sites: SiteModel[]) {
    ConsoleLogger.debug('xxxx - PortalComponent[22].handleAuthenticatedUser(): called');
    if (grants) {
      this.showLandingPage = false;
      this.showAppSubMenu = false;
      this.showDashboard = false;
      this.showSiteList = false;

      let selectActiveApp = false;

      if (this.route.snapshot.queryParams['forceHome']) {
        ConsoleLogger.debug('xxxx - PortalComponent[22.1].handleAuthenticatedUser(): forceHome query param exists');
        this.showLandingPage = true;
        this.showAppSubMenu = true;
        this.showSiteList = true;

        //remove forceHome query param
        this.router.navigate([], {
          queryParams: { forceHome: null },
          queryParamsHandling: 'merge'
        });
        return;
      }

      // 'dai' query param indicates destinyadmin was at a site and clicked DestinyAI link. Since only avail at the district, we needed to navigate to district first.
      if (this.route.snapshot.queryParams['dai'] === 'true' && this.portalStateService.isDestinyAiAvailableForUser()) {
        ConsoleLogger.debug('xxxx - PortalComponent[22.2].handleAuthenticatedUser(): dai query param exists');
        this.portalStateService.goToDestinyAi();
        this.router.navigate([], { queryParams: { dai: null }, queryParamsHandling: 'merge'}); //remove dai query param
        return;
      }

      if (this.userHasAccessToAppParam(grants)) {
        ConsoleLogger.debug('xxxx - PortalComponent[23].handleAuthenticatedUser(): userHasAccessToAppParam true');
         this.portalStateService.setActiveAppFromApplication(this.queryParamApp);
      } else {
        ConsoleLogger.debug('xxxx - PortalComponent[24].handleAuthenticatedUser(): userHasAccessToAppParam false');
        let preferredApp: Application = this.unsService.getUnifiedSettings()?.getPreferredApp();
        preferredApp = this.portalStateService.getSelectableApp(preferredApp)?.app;

        const navParam = this.route.snapshot.queryParams['nav'];

        if (navParam && !this.authService.isGuestUser() && !!this.queryParamApp) {
          ConsoleLogger.debug('xxxx - PortalComponent[25].handleAuthenticatedUser(): navParam exists, not guest, queryParamApp exists');
          const app: Application = Util.getEnumFromString(Application, this.queryParamApp);
          let msg = 'You don\'t have access to ' + this.queryParamApp;
          if(app) {
            msg = 'You don\'t have access to ' + Util.getReadableApplicationName(app);
          }
          this.toastService.show(DialogModeType.ERROR, msg + '. We\'ve redirected you to your default application.', true);
          preferredApp = undefined;
        }

        let skipMe = false;

        if (this.authService.isDestinyAdminNotAtSite()) { // DestinyAdmin @District
          ConsoleLogger.debug('xxxx - PortalComponent[26].handleAuthenticatedUser(): isDestinyAdminNotAtSite true');
          if (this.queryParamApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[26.1].handleAuthenticatedUser(): queryParamApp: ' + this.queryParamApp);
            this.showLandingPage = true;
            this.showAppSubMenu = true;
            this.showSiteList = true;

            skipMe = true;
            this.portalStateService.setActiveAppFromApplication(this.queryParamApp);
          } else {
          if (!preferredApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[27].handleAuthenticatedUser(): preferredApp not set');
            this.showLandingPage = true;
            this.showAppSubMenu = true;
            this.showSiteList = true;
          }
          }
        } else if (this.authService.isDistrictUserNotAtSite()) { // District user @District
          ConsoleLogger.debug('xxxx - PortalComponent[28].handleAuthenticatedUser(): isDistrictUserNotAtSite true');
          if (!preferredApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[29].handleAuthenticatedUser(): preferredApp not set');
            if (_.isEmpty(sites)) {
              ConsoleLogger.debug('xxxx - PortalComponent[30].handleAuthenticatedUser(): sites empty');
              if (this.portalStateService.hasDashboardAccess(grants)) {
                ConsoleLogger.debug('xxxx - PortalComponent[31].handleAuthenticatedUser(): hasDashboardAccess true');
                this.showDashboard = true;
              } else {
                ConsoleLogger.debug('xxxx - PortalComponent[32].handleAuthenticatedUser(): hasDashboardAccess false');
                selectActiveApp = true;
              }
            } else {
              ConsoleLogger.debug('xxxx - PortalComponent[33].handleAuthenticatedUser(): sites not empty');
              this.showLandingPage = true;
              this.showAppSubMenu = true;
              this.showSiteList = true;
            }
          }
        } else if (this.authService.isDistrictUserAtSite()) { // Distirct user @Site
          ConsoleLogger.debug('xxxx - PortalComponent[34].handleAuthenticatedUser(): isDistrictUserAtSite true');
          if (!preferredApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[35].handleAuthenticatedUser(): preferredApp not set');
            this.showDashboard = this.portalStateService.hasDashboardAccess(grants);
            if (this.showDashboard) {
              ConsoleLogger.debug('xxxx - PortalComponent[36].handleAuthenticatedUser(): showDashboard true');
              // If already at district dashboard and going to site dashboard, don't show the app submenu
              this.showAppSubMenu = this.portalStateService.getActiveApp()?.app !== Application.DASHBOARD;
              this.showLandingPage = true;
            } else {
              ConsoleLogger.debug('xxxx - PortalComponent[37].handleAuthenticatedUser(): showDashboard false');
              selectActiveApp = true;
            }
          }
        } else if (this.authService.isSiteAdmin()) { // SiteAdmin
          ConsoleLogger.debug('xxxx - PortalComponent[38].handleAuthenticatedUser(): isSiteAdmin true');
          if (!preferredApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[39].handleAuthenticatedUser(): preferredApp not set');
            if (this.portalStateService.hasDashboardAccess(grants)) {
              ConsoleLogger.debug('xxxx - PortalComponent[40].handleAuthenticatedUser(): hasDashboardAccess true');
              this.showDashboard = true;
              this.showAppSubMenu = true;
              this.showLandingPage = true;
            } else {
              ConsoleLogger.debug('xxxx - PortalComponent[41].handleAuthenticatedUser(): hasDashboardAccess false');
              selectActiveApp = true;
            }
          }
        } else if( this.authService.isGuestUser() && this.queryParamApp === Application.PORTAL) {
          ConsoleLogger.debug('xxxx - PortalComponent[42].handleAuthenticatedUser(): isGuestUser true and queryParamApp is PORTAL');
          this.userService.fetchSitesList(undefined)
            .pipe(
              map((sites: SitesModel) => [grants, sites]),
              catchError(error => {
                ConsoleLogger.error('xxxx - PortalComponent[43].handleAuthenticatedUser(): Failed to get Sites for user: ' + JSON.stringify(error));
                return of([grants, undefined]);
              }),
              takeUntil(this.ngUnsubscribe)
            ).subscribe(() => {
              this.portalStateService.setActiveAppFromScopes(grants);
            });
        } else if( this.authService.isGuestUser() && !this.queryParamApp) {
          ConsoleLogger.debug('xxxx - PortalComponent[44].handleAuthenticatedUser(): isGuestUser true and queryParamApp not set');
          // If the was an Auth event and the siteGuid was not set,
          // this was the guest going from a site to the district page
          // Clear the ULM and SiteGuid and log the user out of AASP
          // then set the active app to the portal as the user will be going to the district page.

          if (!this.authService.getAuthenticationEvent()?.siteGuid) {
            ConsoleLogger.debug('xxxx - PortalComponent[45].handleAuthenticatedUser(): authenticationEvent.siteGuid not set');
            if (this.authService.getSiteGuidParam()) {
              ConsoleLogger.debug('xxxx - PortalComponent[46].handleAuthenticatedUser(): siteGuidParam exists');
              this.authService.setSiteGuid(this.authService.getSiteGuidParam());
              this.authService.setSiteGuidParam(undefined);
              this.handleUndefinedUser();
              selectActiveApp = true;
            } else {
              ConsoleLogger.debug('xxxx - PortalComponent[47].handleAuthenticatedUser(): siteGuidParam not set');
              this.authService.setUserLogin(undefined);
              this.authService.logout('guestToDistrict').pipe(takeUntil(this.ngUnsubscribe)).subscribe();
              this.authService.setSiteGuid(undefined);
              this.portalStateService.setActiveAppFromApplication(Application.PORTAL);
              selectActiveApp = false;
            }
          } else {
            ConsoleLogger.debug('xxxx - PortalComponent[48].handleAuthenticatedUser(): authenticationEvent.siteGuid set');
            // Guest user is clicking on site link on district, need to set the active app for the site
            selectActiveApp = true;
          }
        }else if(this.authService.isSharedUser()){
          ConsoleLogger.debug('xxxx - PortalComponent[49].handleAuthenticatedUser(): isSharedUser true');
          this.portalStateService.setActiveAppFromApplication(Application.DD);
          selectActiveApp = false;
        } else if (this.authService.isDestinyAdminAtSite()
                  || this.authService.isGuestUser()
                  || this.authService.isSharedUser()
                  || this.authService.isNotSiteAdmin()) {
          ConsoleLogger.debug('xxxx - PortalComponent[50].handleAuthenticatedUser(): isDestinyAdminAtSite or isGuestUser or isSharedUser or isNotSiteAdmin true');
          // If this is a Guest, prompt them to login since they don't have access to the app
          if (this.authService.isGuestUser() && this.queryParamApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[51].handleAuthenticatedUser(): isGuestUser true and queryParamApp exists');
            const siteGuidParam = this.route.snapshot.queryParams['siteGuid'];
            if (!siteGuidParam) {
              ConsoleLogger.debug('xxxx - PortalComponent[52].handleAuthenticatedUser(): siteGuidParam not set');
              this.router.navigate(['portal']); // Go to district sites page
            } else {
              ConsoleLogger.debug('xxxx - PortalComponent[53].handleAuthenticatedUser(): siteGuidParam set');
              const skipGuestLoginPrompt = this.initStateService.skipGuestLoginPrompt;
              this.initStateService.skipGuestLoginPrompt = false;
              ConsoleLogger.debug('xxxx - PortalComponent[54].guestLoginAlreadyAttempted: ' + skipGuestLoginPrompt);
              if (!skipGuestLoginPrompt) {
                ConsoleLogger.debug('xxxx - PortalComponent[55].handleAuthenticatedUser(): skipGuestLoginPrompt false');
                const loginAction: LoginAction = {loginPrompt: LoginPrompt.LOGIN, fullscreen: false, app: this.queryParamApp, siteGuid: this.authService.getAuthenticationEvent()?.siteGuid};
                const strippedNav = AuthService.stripNavToken(navParam);
                if(!_.isEmpty(strippedNav)) {
                  loginAction.continuationState = AuthService.CONTINUATION_STATE_NAV_TOKEN + strippedNav;
                }
                loginAction.skipGuestLoginPrompt = true;
                this.authService.login(loginAction, 'guestLoginPrompt');
                return;
              } else {
                ConsoleLogger.debug('xxxx - PortalComponent[56].handleAuthenticatedUser(): skipGuestLoginPrompt true');
                const queryParams: Params = { appId: this.authService.getDistrictAppId(), siteGuid: this.authService.getSiteGuid()};
                this.router.navigate([], {relativeTo: this.route, queryParams}); // remove the 'nav' and 'app' params
              }
            }
          }
          selectActiveApp = true;
        }

        this.authService.setSiteGuidParam(undefined);

        if (!skipMe) {

        if (selectActiveApp) {
          ConsoleLogger.debug('xxxx - PortalComponent[57].handleAuthenticatedUser(): selectActiveApp true');
          if (this.queryParamApp === Application.CLM && this.portalStateService.hasClassroomLibraryAccess(grants)) {
            ConsoleLogger.debug('xxxx - PortalComponent[58].handleAuthenticatedUser(): queryParamApp is CLM and hasClassroomLibraryAccess true');
            this.portalStateService.setActiveAppFromApplication(Application.CLM);
          } else {
            ConsoleLogger.debug('xxxx - PortalComponent[59].handleAuthenticatedUser(): setActiveAppFromScopes');
            this.portalStateService.setActiveAppFromScopes(grants);
          }
        } else {
          ConsoleLogger.debug('xxxx - PortalComponent[60].handleAuthenticatedUser(): selectActiveApp false');

          if (preferredApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[61].handleAuthenticatedUser(): preferredApp set');
            this.portalStateService.setActiveAppFromApplication(preferredApp);
          } else {
            ConsoleLogger.debug('xxxx - PortalComponent[62].handleAuthenticatedUser(): preferredApp not set');
            if (this.showDashboard) {
              ConsoleLogger.debug('xxxx - PortalComponent[63].handleAuthenticatedUser(): showDashboard true');
              this.portalStateService.setActiveAppFromApplication(Application.DASHBOARD);
            } else {
              ConsoleLogger.debug('xxxx - PortalComponent[64].handleAuthenticatedUser(): showDashboard false');
              this.router.navigate([], {queryParams: { appId: this.authService.getDistrictAppId()}});
            }
          }
          }

        }
      }
    }
  }


  private handleActiveApp() {
    ConsoleLogger.debug('xxxx - PortalComponent[65].handleActiveApp(): called');
    this.portalStateService.getActiveApp$()
      .pipe(
        startWith(undefined),
        pairwise(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([prevApp, activeApp]) => {
        ConsoleLogger.debug('xxxx - PortalComponent[66].handleActiveApp(): subscribe prevApp=' + JSON.stringify(prevApp) + ', activeApp=' + JSON.stringify(activeApp));
        this.showAppSubMenu = false;
        this.showLandingPage = false;
        ConsoleLogger.debug('xxxx - PortalComponent[67].handleActiveApp(): ' +  activeApp.app);
        if (Application.PORTAL === activeApp?.app) { // Definitely at the District
          ConsoleLogger.debug('xxxx - PortalComponent[68].handleActiveApp(): activeApp is PORTAL');
          this.visibleApp = Application.PORTAL;
          if (!prevApp && this.queryParamApp) { // First time loading the app if prevApp is undefined. If there's an app param, go to that app
            ConsoleLogger.debug('xxxx - PortalComponent[69].handleActiveApp(): prevApp not set and queryParamApp exists');
            const pa: PortalApp = this.portalStateService.getSelectableApp(this.queryParamApp);
            const qps: any = this.route.snapshot.queryParams;

            if (!pa && !qps.siteGuid && qps.nav) {
              ConsoleLogger.debug('xxxx - PortalComponent[70].handleActiveApp(): pa not set, siteGuid not set, nav param exists');
              const ulm = this.authService.getUserLogin();
              if (!ulm?.isDistrictUser() && !ulm?.isDestinyAdmin()) {
                ConsoleLogger.debug('xxxx - PortalComponent[71].handleActiveApp(): not district user and not destiny admin');
                const loginAction: LoginAction = {loginPrompt: LoginPrompt.LOGIN, fullscreen: false, app: this.queryParamApp, siteGuid: this.authService.getAuthenticationEvent()?.siteGuid};
                loginAction.continuationState = AuthService.CONTINUATION_STATE_NAV_TOKEN + AuthService.stripNavToken(qps.nav);
                loginAction.skipGuestLoginPrompt = true;

                // has a nav param and no siteGuid, so treat as a district bookmark
                if (!this.route.snapshot.queryParams['siteGuid']) {
                  ConsoleLogger.debug('xxxx - PortalComponent[72].handleActiveApp(): siteGuid not set in queryParams');
                  loginAction.ignorePreferredSite = true;
                }

                this.authService.login(loginAction, 'activeAppNavParam');
              }
            }

            this.portalStateService.setActiveApp(pa);
          } else {
            ConsoleLogger.debug('xxxx - PortalComponent[73].handleActiveApp(): prevApp set or queryParamApp not set. queryParams: ' + JSON.stringify(this.route.snapshot.queryParams));
            const isDistrictUserAtDistrict = this.authService.isDistrictUserNotAtSite() || this.authService.isDestinyAdminNotAtSite();
            this.showSiteList = !_.isEmpty(this.userService.getEnrolledSitesInDistrict());
            this.showAppSubMenu = isDistrictUserAtDistrict;
            this.showLandingPage = isDistrictUserAtDistrict;
            const app = this.route.snapshot.queryParams['app'];
            const districtAppId = this.route.snapshot.queryParams['appId'];
            const qps: any = {};
            if (districtAppId) {
              qps.appId = districtAppId;
            }
            if (app) {
              qps.app = app;
            }
            this.router.navigate([], { queryParams: qps, relativeTo: this.route});
          }
        } else { // At Site or in Dashboard or DBO at the District
          ConsoleLogger.debug('xxxx - PortalComponent[74].handleActiveApp(): activeApp is not PORTAL');
          this.visibleApp = activeApp?.app || Application.PORTAL;
          this.showLandingPage = false;

          if (Application.DASHBOARD !== activeApp?.app) { // See D-37168
            ConsoleLogger.debug('xxxx - PortalComponent[75].handleActiveApp(): activeApp is not DASHBOARD');
            this.showAppSubMenu = false;
          }

          const appId = this.authService.getDistrictAppId();
          const siteGuid = this.authService.getSiteGuid();
          const queryParams: any = {
            app: activeApp?.app,
            appId: appId
          };
          if (siteGuid) {
            queryParams.siteGuid = siteGuid;
          }
          if (!prevApp || Application.PORTAL !== prevApp.app && prevApp !== activeApp) {
            ConsoleLogger.debug('xxxx - PortalComponent[76].handleActiveApp(): prevApp not set or prevApp not PORTAL and prevApp != activeApp');
            queryParams.nav = null;
          }

          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: queryParams,
            queryParamsHandling: 'merge' // remove to replace all query params by provided
          });
        }
      });
  }

  private handleQueryParams() {
    ConsoleLogger.debug('xxxx - PortalComponent[77].handleQueryParams(): called');
    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        ConsoleLogger.debug('xxxx - PortalComponent[78].handleQueryParams(): subscribe params=' + JSON.stringify(params));
        this.queryParamApp = params['app'];
      });
  }

  private handlePopupState() {
    ConsoleLogger.debug('xxxx - PortalComponent[79].handlePopupState(): called');
    this.clientLoginService.getPopupState$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe( state => {
        ConsoleLogger.debug('xxxx - PortalComponent[80].handlePopupState(): subscribe state=' + state);
        this.loginPopupOpen = state === PopupState.LOGIN;
      });
  }


  public userHasAccessToAppParam(grants: GrantsModel) {
    return this.queryParamApp && this.portalStateService.isApplicationAvailableForGrantsWithUser(this.queryParamApp, grants, this.authService.getUserLogin());
  }


  public isIframeHidden(app: PortalApp) {
    let hideApp = app.app !== this.visibleApp || app.accessible === false;
    if (_.isEmpty(this.destinyUserResetRequestReason)) {  // IF this is a reset request, don't show landing page
      if (this.showLandingPage) { // on landing page
        if (!this.showDashboard) { // only show if showDashboard
          hideApp = true;
        }
      }
    }
    return hideApp;
  }


  public goToLoginPopup() {
    this.clientLoginService.setFocus();
  }


  public skipToMainContent() {
    const element = document.querySelector('[role="main"]') as HTMLElement;
    element?.setAttribute('tabindex', '-1');
    element?.focus();
  }
}
